import { ChartDatasetModel } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  input,
} from '@angular/core';
import { Options } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';

@Component({
  selector: 'procure-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss',
  imports: [HighchartsChartComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarChart {
  readonly dataset = input<ChartDatasetModel | null | undefined>();
  readonly suffix = input<string>();
  readonly multiColor = input<boolean>(false);

  readonly horizontal = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  // eslint-disable-next-line max-lines-per-function
  protected readonly chartOptions = computed<Options>(() => {
    const suffix = this.suffix() ? ` ${this.suffix()}` : '';
    const data = this.dataset()?.data ?? [];
    const label = this.dataset()?.label ?? '';

    return !this.horizontal()
      ? {
          credits: {
            enabled: false,
          },
          title: {
            text: '',
          },
          chart: {
            type: 'column',
            styledMode: true,
            height: 200,
          },
          legend: {
            enabled: false,
          },
          series: [
            {
              type: 'column',
              name: label ?? '',
              colorByPoint: this.multiColor(),
              data: data
                ?.map((item) => item.value)
                .map((value, i) => ({
                  y: value,
                  colorCount: i,
                })),
            },
          ],
          xAxis: {
            title: {
              text: '',
            },
            categories: data?.map((item) => item.name ?? ''),
          },
          yAxis: {
            title: {
              text: '',
            },
            labels: {
              format: `{value}${suffix}`,
            },
            tickPixelInterval: 50,
            minTickInterval: 1,
            gridLineWidth: 1,
          },
          tooltip: {
            formatter(): string {
              return `<b>${this.key}</b><br/><span style="color: ${this.series.color}">\u25CF</span> ${this.series.name}: ${this.y}${suffix}`;
            },
          },
        }
      : {
          credits: {
            enabled: false,
          },
          title: {
            text: '',
          },
          chart: {
            type: 'bar',
            height: 60,
            margin: 0,
          },
          legend: {
            enabled: false,
          },
          plotOptions: {
            bar: {
              pointPadding: 0,
              pointWidth: 60,
            },
            series: {
              stacking: 'normal',
              dataLabels: {
                enabled: true,
                formatter(): string {
                  return '';
                },
              },
            },
          },
          series: data.map((item, i) => ({
            type: 'bar',
            name: item.name ?? '',
            data: [item.value],
            color: i === 0 ? 'grey' : undefined,
          })),
          xAxis: {
            title: {
              text: '',
            },
            visible: false,
            labels: {
              rotation: 0,
            },
            categories: [this.dataset()?.label ?? ''],
          },
          yAxis: {
            title: {
              text: '',
            },
            visible: false,
            labels: {
              format: `{value}${suffix}`,
            },
            max: data.reduce((acc, item) => acc + item.value, 0),
            endOnTick: false,
          },
          tooltip: {
            style: {
              color: 'white',
            },
            formatter(): string {
              return `<b>${this.key}</b><br/><span style="color: ${this.series.color}">\u25CF</span> ${this.series.name}: ${this.y}${suffix}`;
            },
          },
        };
  });
}
